"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CARDLESS_MID = exports.PaymentMethod = exports.TransactionRewardsType = exports.TransactionType = exports.TransactionStatus = exports.TransactionsEndpoints = exports.MerchantCategory = exports.DisputeStatus = exports.DisputeReason = exports.DisputeEligibility = exports.DisputeCardStatus = exports.DigitalWalletType = void 0;
/** Cardless merchant id; same in staging + production */
const CARDLESS_MID = '4045998259';
exports.CARDLESS_MID = CARDLESS_MID;
var TransactionsEndpoints;
(function (TransactionsEndpoints) {
    TransactionsEndpoints["CreateDispute"] = "/transactions/create-dispute";
    TransactionsEndpoints["GetNotificationSettings"] = "/transactions/get-notification-settings";
    TransactionsEndpoints["GetTransactions"] = "/transactions/get-transactions";
    TransactionsEndpoints["UpdateNotificationSettings"] = "/transactions/update-notification-settings";
    TransactionsEndpoints["GetTransactionsExportedURL"] = "/transactions/exported-url";
    TransactionsEndpoints["ExportTransactions"] = "/transactions/export";
    TransactionsEndpoints["GetTransactionById"] = "/transactions/get-transaction-by-id";
    TransactionsEndpoints["GetTransactionByIdV2"] = "/transactions/v2/get-transaction-by-id";
})(TransactionsEndpoints || (exports.TransactionsEndpoints = TransactionsEndpoints = {}));
/*
 * Statuses in the transaction status lifecycle.
 * See https://docs.google.com/document/d/1cHhtzvJxz4vb28q0CgEqor8nn9y0XOjYyWmI9H-RgOI/edit?usp=sharing for more info.
 */
var TransactionStatus;
(function (TransactionStatus) {
    TransactionStatus["Unspecified"] = "TRANSACTION_STATUS_UNSPECIFIED";
    /** Transaction is pending. Current balance has not been modified yet. */
    TransactionStatus["Pending"] = "PENDING";
    /**
     * Transaction was settled and the account's current balance has been modified with the processor.
     * This is a valid end state of the transaction lifecycle.
     */
    TransactionStatus["Settled"] = "SETTLED";
    /**
     * Auth was declined.
     * This transaction doesn't affect the account's current balance or credit limit, because it effectively never happened.
     * Useful for showing the user when their transaction was declined.
     */
    TransactionStatus["Declined"] = "DECLINED";
    /**
     * Auth expired.
     * This transaction doesn't affect the account's current balance and their credit limit is free up.
     * Auths can expire for a variety of reasons.
     *   e.g. I buy a burrito at Chipotle (successful auth), eat it, then the next day the auth expires due to a merchant issue.
     *     I just got a free burrito which I don't have to then pay for.
     */
    TransactionStatus["AuthExpired"] = "AUTH_EXPIRED";
})(TransactionStatus || (exports.TransactionStatus = TransactionStatus = {}));
/*
 * These are the different types of transactions that are stored in our system.
 * The transaction type informs what TransactionStatuses are valid for a given transaction.
 */
var TransactionType;
(function (TransactionType) {
    TransactionType["Unspecified"] = "TRANSACTION_TYPE_UNSPECIFIED";
    /**
     * A normal purchase from a merchant, e.g. buy a coffee.
     *
     * A PURCHASE starts with an authorization, which can be either approved or declined.
     *
     * If the auth is approved, the transaction will be in the PENDING state, until it settles,
     * in which case it will end in the SETTLED state.
     *
     * The available credit of the account will decrease when this transaction is authorized.
     * The available credit of the account will possibly change when this transaction is settled,
     * if the authorized amount is different from the settled amount.
     * The current balance of the account will increase once this transaction is settled.
     *
     * If declined, the transaction will start and remain in the DECLINED state.
     * It will not affect the balance or available credit.
     */
    TransactionType["Purchase"] = "PURCHASE";
    /**
     * A payment to the account, e.g. ACH to pay off current balance.
     *
     * A PAYMENT transaction will be immediately initiated, but the funds are "on hold" until the payment goes through.
     * During this "hold" period (usually ~3 days), the transaction will be PENDING, and once the "hold" is lifted
     * it will be SETTLED. During the "hold" period, a payment can be DECLINED.
     *
     * The available credit of the account will increase when this transaction is settled.
     * The current balance of the account will decrease when this transaction is initiated.
     */
    TransactionType["Payment"] = "PAYMENT";
    /**
     * A transaction is refunded by the merchant, e.g. return a shirt and get refunded by Nordstrom.
     *
     * A REFUND transaction has the same status lifecycle as a PURCHASE.
     *
     * The available credit of the account will increase when this transaction is settled.
     * The current balance of the account will decrease once this transaction settles.
     *
     * A REFUND transaction will be a different transaction object than the original PURCHASE transaction.
     */
    TransactionType["Refund"] = "REFUND";
    /**
     * A transaction chargeback is processed.
     *
     * A CHARGEBACK transaction will be immediately authorized, but it still takes time
     * to reach the settled state. TODO verify with CoreCard if this is true
     *
     * The available credit of the account will increase when this transaction is settled.
     * The current balance of the account will decrease once this transaction settles.
     *
     * A CHARGEBACK transaction will be a different transaction object than the original PURCHASE transaction.
     */
    TransactionType["Chargeback"] = "CHARGEBACK";
    /**
     * A fee for the account, e.g. annual fee for a premium card.
     *
     * A FEE transaction will start in the SETTLED state.
     *
     * The available credit of the account will decrease.
     * The current balance of the account will increase.
     */
    TransactionType["Fee"] = "FEE";
    /**
     * A credit to the statement balance, e.g. a rewards redemption.
     *
     * A STATEMENT_CREDIT transaction will start in the SETTLED state.
     *
     * The available credit of the account will decrease.
     * The current balance of the account will decrease.
     */
    TransactionType["StatementCredit"] = "STATEMENT_CREDIT";
    /**
     * Interest charge was added to the account, e.g. interest accrued from an account carrying a balance.
     *
     * An INTEREST transaction will start in the SETTLED state.
     *
     * The available credit will decrease. (TODO verify w/ CC if this is true)
     * The current balance of the account will increase.
     */
    TransactionType["Interest"] = "INTEREST";
    /**
     * Balance adjustment to correct an error.
     *
     * AN ADJUSTMENT transaction will start in the SETTLED state.
     *
     * There is no guarantee on how this type of transaction affects available credit and current balance.
     */
    TransactionType["Adjustment"] = "ADJUSTMENT";
    /**
     * Reversal of previous transaction.
     *
     * A REVERSAL transaction will start in the SETTLED state.
     *
     * There is no guarantee on how this type of transaction affects available credit and current balance.
     */
    TransactionType["Reversal"] = "REVERSAL";
    /**
     * Dispute of a purchase transaction.
     *
     * A DISPUTE transaction will start in the SETTLED state.
     *
     * The available credit will increase.
     * The current balance of the account will decrease.
     * For more info: https://coretrack.corecard.com/edit_bug.aspx?id=102561
     */
    TransactionType["Dispute"] = "DISPUTE";
    /**
     * Merchant offers from American Express that will appear as debits, but we do not want to deduct points for them.
     *
     * An OFFER transaction will start in the SETTLED state.
     *
     * The available credit will increase.
     * The current balance of the account will decrease.
     * For more info: https://coretrack.corecard.com/edit_bug.aspx?id=110002
     */
    TransactionType["Offer"] = "OFFER";
    /**
     * Cash advance from an ATM.
     * A CASH_ADVANCE transaction will start in the PENDING state.
     *
     * The available credit of the account will decrease when this transaction is authorized.
     */
    TransactionType["CashAdvance"] = "CASH_ADVANCE";
})(TransactionType || (exports.TransactionType = TransactionType = {}));
/**
 * The merchant category that a PURCHASE or REFUND transaction falls under.
 * MerchantCategory is defined by the MCC code.
 * For more info: https://www.notion.so/MCC-Codes-69dde4f01fed4f188736e04aec28c503
 */
var MerchantCategory;
(function (MerchantCategory) {
    MerchantCategory["Unspecified"] = "MERCHANT_CATEGORY_UNSPECIFIED";
    /** The transaction's mcc code doesn't fall under any of our defined categories. */
    MerchantCategory["Other"] = "OTHER";
    /**
     * Spending within the brand associated with this account.
     * This will override the specific MCC code that a transaction falls under.
     * e.g. if we had an Airbnb card, an Airbnb purchase would be categorized as BRAND spend under an Airbnb account, not as TRAVEL.
     */
    MerchantCategory["Brand"] = "BRAND";
    // TODO these categories can change, just wanted to put them here for testing purposes as we start to build out transactions/rewards.
    MerchantCategory["Airline"] = "AIRLINES";
    MerchantCategory["CarRental"] = "CAR_RENTAL";
    MerchantCategory["Dining"] = "DINING";
    MerchantCategory["Fitness"] = "FITNESS";
    MerchantCategory["FoodDelivery"] = "FOOD_DELIVERY";
    MerchantCategory["Gas"] = "GAS";
    MerchantCategory["GiftCard"] = "GIFT_CARD";
    MerchantCategory["Groceries"] = "GROCERIES";
    MerchantCategory["Hotel"] = "HOTEL";
    MerchantCategory["Pharmacy"] = "PHARMACY";
    MerchantCategory["Shopping"] = "SHOPPING";
    MerchantCategory["Streaming"] = "STREAMING";
    MerchantCategory["Transit"] = "TRANSIT";
    MerchantCategory["Travel"] = "TRAVEL";
    MerchantCategory["Utilities"] = "UTILITIES";
    MerchantCategory["Wholesale"] = "WHOLESALE";
})(MerchantCategory || (exports.MerchantCategory = MerchantCategory = {}));
/**
 * Type of digital wallet used to initiate a transaction, instead of a physical/virtual card.
 */
var DigitalWalletType;
(function (DigitalWalletType) {
    DigitalWalletType["Unspecified"] = "DIGITAL_WALLET_TYPE_UNSPECIFIED";
    DigitalWalletType["ApplePay"] = "APPLE_PAY";
    DigitalWalletType["GooglePay"] = "GOOGLE_PAY";
})(DigitalWalletType || (exports.DigitalWalletType = DigitalWalletType = {}));
/**
 * A derived type that consolidates the four types of payment methods for a transaction.
 * This enum doesn't exist in the backend, but is a convenience enum for use in the frontend.
 */
var PaymentMethod;
(function (PaymentMethod) {
    PaymentMethod["Physical"] = "PHYSICAL";
    PaymentMethod["Virtual"] = "VIRTUAL";
    PaymentMethod["ApplePay"] = "APPLE_PAY";
    PaymentMethod["GooglePay"] = "GOOGLE_PAY";
})(PaymentMethod || (exports.PaymentMethod = PaymentMethod = {}));
var TransactionRewardsType;
(function (TransactionRewardsType) {
    TransactionRewardsType["REWARDS_TYPE_UNSPECIFIED"] = "REWARDS_TYPE_UNSPECIFIED";
    TransactionRewardsType["POINTS"] = "POINTS";
    TransactionRewardsType["PAYMENT_PLAN"] = "PAYMENT_PLAN";
})(TransactionRewardsType || (exports.TransactionRewardsType = TransactionRewardsType = {}));
/** Card status at the time of dispute */
var DisputeCardStatus;
(function (DisputeCardStatus) {
    /** Status is not specified */
    DisputeCardStatus["CardStatusUnspecified"] = "CARD_STATUS_UNSPECIFIED";
    /** Card was reported lost */
    DisputeCardStatus["Lost"] = "LOST";
    /** Card was reported stolen */
    DisputeCardStatus["Stolen"] = "STOLEN";
    /** Card is in possession of cardholder */
    DisputeCardStatus["InPossession"] = "IN_POSSESSION";
})(DisputeCardStatus || (exports.DisputeCardStatus = DisputeCardStatus = {}));
var DisputeReason;
(function (DisputeReason) {
    DisputeReason["Unspecified"] = "DISPUTE_REASON_UNSPECIFIED";
    DisputeReason["UnrecognizedCharge"] = "UNRECOGNIZED_CHARGE";
    DisputeReason["ChargedMultipleTimes"] = "CHARGED_MULTIPLE_TIMES";
    DisputeReason["Overcharged"] = "OVERCHARGED";
    DisputeReason["ItemNotReceived"] = "ITEM_NOT_RECEIVED";
    DisputeReason["ItemReceivedLate"] = "ITEM_RECEIVED_LATE";
    DisputeReason["ItemOrServiceNotAsDescribed"] = "ITEM_OR_SERVICE_NOT_AS_DESCRIBED";
    DisputeReason["ChargedForCancelledSubscription"] = "CHARGED_FOR_CANCELLED_SUBSCRIPTION";
    DisputeReason["NoRefundForReturnedItem"] = "NO_REFUND_FOR_RETURNED_ITEM";
})(DisputeReason || (exports.DisputeReason = DisputeReason = {}));
/** Current state of the dispute lifecycle */
var DisputeStatus;
(function (DisputeStatus) {
    DisputeStatus["Unspecified"] = "DISPUTE_STATE_UNSPECIFIED";
    /** Cardholder just filed */
    DisputeStatus["Initiated"] = "INITIATED";
    /** Ticket opened */
    DisputeStatus["Received"] = "RECEIVED";
    /** Ops/processor working */
    DisputeStatus["UnderReview"] = "UNDER_REVIEW";
    /** Resolved in favor of merchant */
    DisputeStatus["Merchant"] = "MERCHANT";
    /** Resolved in favor of cardholder */
    DisputeStatus["Cardholder"] = "CARDHOLDER";
})(DisputeStatus || (exports.DisputeStatus = DisputeStatus = {}));
/** Dispute eligibility status */
var DisputeEligibility;
(function (DisputeEligibility) {
    /** Default / unspecified status */
    DisputeEligibility["Unspecified"] = "DISPUTE_UNSPECIFIED";
    /** Dispute is eligible */
    DisputeEligibility["Eligible"] = "ELIGIBLE";
    /** Account is ineligible for disputes */
    DisputeEligibility["AccountIneligible"] = "ACCOUNT_INELIGIBLE";
    /** Transaction is ineligible for disputes */
    DisputeEligibility["TransactionIneligible"] = "TRANSACTION_INELIGIBLE";
})(DisputeEligibility || (exports.DisputeEligibility = DisputeEligibility = {}));
